import React from 'react';
import { Link } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL, SITE_URL } from '../../constants/brand';
import type { CityDef } from '../../data/cities';
import { CORE_CITIES, hasServiceCityPage } from '../../data/cities';
import type { ServiceDef } from '../../data/services';
import { SERVICES } from '../../data/services';
import { locationPath, ROUTES, serviceCityPath, servicePath } from '../../enum/routes';
import CtaBand from '../CtaBand';
import FaqSection from '../FaqSection';
import Seo from '../Seo';

export default function ServiceCityPageTemplate({
  service,
  city,
}: {
  service: ServiceDef;
  city: CityDef;
}) {
  const notes = city.serviceNotes[service.slug];
  if (!notes) return null;

  const path = serviceCityPath(service.slug, city.slug);
  const otherCore = CORE_CITIES.filter((c) => c.slug !== city.slug);
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${service.name} in ${city.name}, ${city.state}`,
    description: notes.metaDescription,
    url: `${SITE_URL}${path}`,
    provider: {
      '@type': 'Organization',
      name: 'Epic Home Pros',
      telephone: PHONE_TEL,
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'City',
      name: city.name,
      containedInPlace: { '@type': 'State', name: 'Texas' },
    },
  };

  return (
    <>
      <Seo title={notes.metaTitle} description={notes.metaDescription} path={path} jsonLd={schema} />

      <section className="relative isolate flex min-h-[55vh] w-full items-center overflow-hidden md:min-h-[62vh]">
        <img alt={service.heroImageAlt} className="absolute inset-0 h-full w-full object-cover" src={service.heroImage} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/70" aria-hidden="true" />
        <div className="container-page relative z-10 py-20 text-white">
          <div className="max-w-3xl">
            <div className="label-eyebrow !text-white/90">
              {city.name}, {city.state}
            </div>
            <h1 className="h-display mt-4 text-white drop-shadow">
              {service.name} in {city.name}
            </h1>
            <p className="mt-5 max-w-2xl text-base text-white/90 sm:text-lg">{notes.heroLead}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link className="btn-primary" to={ROUTES.QUOTE}>
                Get a Free Quote
              </Link>
              <a
                href={`tel:${PHONE_TEL}`}
                className="btn-secondary border-white bg-white/10 text-white hover:bg-white hover:text-forest"
              >
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl space-y-6">
            {notes.paragraphs.map((p) => (
              <p key={p.slice(0, 40)} className="leading-relaxed text-ink/80">
                {p}
              </p>
            ))}
            <ul className="list-disc space-y-2 pl-5 text-ink/80 marker:text-forest">
              {notes.localBullets.map((b) => (
                <li key={b} className="leading-relaxed">
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl space-y-6">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">How your quote works in {city.name}</h2>
            <ol className="space-y-4">
              {service.processSteps.map((step) => (
                <li key={step.label} className="rounded-lg border border-black/5 bg-white p-5 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-forest">{step.label}</div>
                  <p className="mt-2 text-ink/80">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div>
            <h2 className="font-serif text-2xl text-ink">{service.shortName} in other Texas cities</h2>
            <ul className="mt-5 space-y-2">
              {otherCore.map((c) =>
                hasServiceCityPage(service.slug, c.slug) ? (
                  <li key={c.slug}>
                    <Link className="font-medium text-forest hover:underline" to={serviceCityPath(service.slug, c.slug)}>
                      {service.name} in {c.name}
                    </Link>
                  </li>
                ) : null
              )}
              <li>
                <Link className="font-medium text-forest hover:underline" to={servicePath(service.slug)}>
                  All {service.name} info
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h2 className="font-serif text-2xl text-ink">More services in {city.name}</h2>
            <ul className="mt-5 space-y-2">
              {otherServices.map((s) => (
                <li key={s.slug}>
                  <Link
                    className="font-medium text-forest hover:underline"
                    to={
                      hasServiceCityPage(s.slug, city.slug)
                        ? serviceCityPath(s.slug, city.slug)
                        : locationPath(city.slug)
                    }
                  >
                    {s.name} in {city.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link className="font-medium text-forest hover:underline" to={locationPath(city.slug)}>
                  All {city.name} remodeling
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <FaqSection
        title={`${service.shortName} questions`}
        items={service.faqs.slice(0, 3).concat(city.faqs.slice(0, 1))}
      />
      <CtaBand
        title={`Ready for a ${service.shortName.toLowerCase()} quote in ${city.name}?`}
        lead="Free estimate. No obligation."
      />
    </>
  );
}
